import { Store } from '@ngrx/store';
import { APP_CONFIG } from '../../config/config';
import { HttpClient } from '@angular/common/http';
import { Subscription, lastValueFrom } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { CoreService } from '../../services/core.service';
import * as AppActions from '@app/store/actions/app.actions';
import { IAppConfig } from '@app/store/models/config.interface';
import { PaymentService } from '../../services/payment.service';
import { FunctionsService } from '../../services/functions.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import * as AuthActions from '../../../auth/store/actions/auth.actions';
import { LoginTypes, SubscribeUnsubscribeNewsletterModes } from 'shared-library';

@Component({
    selector: 'app-core-fallback',
    templateUrl: './core-fallback.component.html',
    styleUrls: ['./core-fallback.component.scss']
})
export class CoreFallbackComponent implements OnInit, OnDestroy {

    code: string;
    reason: FallbackReasons;
    private subscriptions: Subscription[] = []

    constructor(
        private store: Store,
        private router: Router,
        private route: ActivatedRoute,
        private httpClient: HttpClient,
        public coreService: CoreService,
        private paymentService: PaymentService,
        public functionService: FunctionsService,
        @Inject(APP_CONFIG) private readonly config: IAppConfig
    ) {
        this.subscriptions.push(this.route.queryParams.subscribe(params => {
            this.code = params['code'];
            this.reason = params['reason'];
        }))
    }

    ngOnDestroy(): void {
        this.subscriptions.map(s => s.unsubscribe())
    }

    ngOnInit(): void {
        switch (this.reason) {
            case FallbackReasons.email_confirmation:
                if (!this.code) {
                    this.kickOut()
                }
                this.handleAccountConfirmationCode()
                break;
            case FallbackReasons.password_reset:
                if (!this.code) {
                    this.kickOut()
                }
                this.handlePasswordResetCode()
                break;
            case FallbackReasons.workspace_invitation:
                if (!this.code) {
                    this.kickOut()
                }
                this.handleWorkspaceInvitation()
                break;
            case FallbackReasons.login:
                if (!this.code) {
                    this.kickOut()
                }
                this.handleLoginWithLink()
                break;
            case FallbackReasons.resume_shopping:
                if (!this.code) {
                    this.kickOut()
                }
                this.handleShoppingResumption()
                break;
            case FallbackReasons.do_not_disturb:
            case FallbackReasons.tell_me_relevant_updates:
            case FallbackReasons.tell_me_promotional_updates:
                if (!this.code) {
                    this.kickOut()
                }
                this.handleMarketingEmailUnsubscribe()
                break;
            default:
                this.kickOut()
                break;
        }
    }

    kickOut() {
        this.router.navigateByUrl("/404")
    }

    async handleAccountConfirmationCode() {
        try {
            await lastValueFrom(this.httpClient.patch(this.config.apiEndpoints.auth.signup, { code: this.code }))
            this.functionService.showSuccessMessage("Account confirmed successfully")
            this.router.navigateByUrl(`/auth/signin`)
            return
        } catch (error) {
        }
        this.kickOut()
    }

    async handleShoppingResumption() {
        try {
            const response = await this.paymentService.resumeAlaCarteSession(this.code)
            this.store.dispatch(AppActions.initialiseAlaCarteSessionSuccess({ response }))
            this.router.navigateByUrl("/cart")
            return
        } catch (error) {
            this.functionService.logToConsole(error)
        }
        this.router.navigateByUrl("/products/memories")
    }

    async handleMarketingEmailUnsubscribe() {
        try {
            await this.coreService.unsubscribeFromNewsletter(this.reason as unknown as SubscribeUnsubscribeNewsletterModes, this.code)
            this.router.navigateByUrl(`/newsletters/unsubscribed`)
            return
        } catch (error) {
            console.log(error)
        }
        this.kickOut()
    }

    async handlePasswordResetCode() {
        this.router.navigateByUrl(`/auth/resetpass?code=${this.code}`)
    }

    async handleWorkspaceInvitation() {
        this.router.navigateByUrl(`/auth/invitation?code=${this.code}`)
    }

    async handleLoginWithLink() {
        if (this.coreService.isLoggedIn()) {
            await this.coreService.logMeOut(false)
        }
        this.store.dispatch(AuthActions.login({
            data: {
                otp: "",
                email: "",
                password: "",
                referral_id: "",
                id_token: this.code,
                activating_ots_qr: "",
                activation_code_ots_qr: "",
                login_type: LoginTypes.LINK,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                recaptcha_token: await this.coreService.getRecaptchaToken(),
            }
        }))
    }
}

enum FallbackReasons {
    login = "login",
    password_reset = "password_reset",
    resume_shopping = "resume_shopping",
    email_confirmation = "email_confirmation",
    workspace_invitation = "workspace_invitation",
    do_not_disturb = "do_not_disturb",
    tell_me_relevant_updates = "tell_me_relevant_updates",
    tell_me_promotional_updates = "tell_me_promotional_updates",
}
